import { useState } from 'react';
import {
  Button,
  Modal,
  Paragraph,
  Stack,
  Spinner,
} from '@contentful/f36-components';
import { config } from '../config/app-config';
import useMsalToken from '../hooks/useMsalToken';

const handlePushToFTA = async (entryId, sdk, setIsLoading, setIsModalOpen, accessToken) => {
  if (!accessToken) {
    sdk.notifier.error('Authentication token not available. Please wait and try again.');
    return;
  }

  setIsLoading(true);

  try {
    const mode = sdk.parameters.instance.mode;
    const apiUrl = mode === 'prod' ? config.prod.api : config.std.api;
    const response = await fetch(`${apiUrl}/content/migration/article`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ entryId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to initiate migration');
    }

    sdk.notifier.success('Migration initiated successfully.');
    setIsModalOpen(false);
  } catch (error) {
    sdk.notifier.error(
      error.message || 'An error occurred while pushing to FTA.'
    );
  } finally {
    setIsLoading(false);
  }
};

const ArticleMigration = ({ sdk, entryId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const accessToken = useMsalToken();

  return (
    <Stack flexDirection="column" alignItems='center' justifyContent='center' style={{ height: '100vh' }}>
      <Button
        variant="primary"
        onClick={() => setIsModalOpen(true)}
        isDisabled={isLoading || !accessToken}
        startIcon={isLoading ? <Spinner size="small" /> : null}
      >
        {!accessToken ? 'Authenticating...' : isLoading ? 'Pushing...' : 'Push to FTA'}
      </Button>

      <Modal
        onClose={() => setIsModalOpen(false)}
        isShown={isModalOpen}
        title="Confirm Push to FTA"
        size="fullscreen"
      >
        {() => (
          <>
            <Modal.Content style={{ padding: '0.5rem' }}>
              <Paragraph marginBottom="none" style={{ textAlign: 'center' }} fontSize="fontSizeS">
                Are you sure you want to push this Article to FTA? This will overwrite existing content.
              </Paragraph>
            </Modal.Content>
            <Modal.Controls>
              <Button
                variant="transparent"
                onClick={() => setIsModalOpen(false)}
                size='small'
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="positive"
                onClick={() => handlePushToFTA(entryId, sdk, setIsLoading, setIsModalOpen, accessToken)}
                size='small'
                isDisabled={isLoading || !accessToken}
                isLoading={isLoading}
              >
                {!accessToken ? 'Authenticating...' : isLoading ? 'Pushing' : 'Confirm'}
              </Button>
            </Modal.Controls>
          </>
        )}
      </Modal>
    </Stack>
  );
};

export default ArticleMigration;
